import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import GooglePlacesDropdown from './GooglePlaces';
import * as UtilsGoogleMaps from './UtilsGoogleMaps';
import JobTabsData from './JobTabs';
import type { Branch, GenericList } from './models/JobSharedInterfaces';
import type { JobDetails, AddressDetails, AddContact } from './models/JobInterfaces';
import type { CustomerContactList } from './models/CustomerInterfaces';
import JobDetailsAddContactModalData from './JobDetailsAddContactModal';
import JobDetailsTransferJobModalData from './JobDetailsTransferJob';
import CancelJobPopup from './CancelJobPopup';
import { GetQuery, PostQuery } from './Shared';

const JobDetailData = () => {
    let navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [systemCompanyId, setSystemCompanyId] = React.useState(2);
    const [showCancelModal, setShowCancelModal] = React.useState(false);
    const [showAddContactModal, setShowAddContactModal] = React.useState(false);
    const [customerContacts, setCustomerContacts] = React.useState<CustomerContactList[]>([]);
    const [branches, setBranches] = React.useState<Branch[]>([]);
    const [yesNoOptions, setYesNoOptions] = React.useState<GenericList[]>([]);
    const [jobTypeOptions, setJobTypeOptions] = React.useState<GenericList[]>([]);
    const [addNewContact, setAddNewContact] = React.useState<AddContact>({ customerId: -1, customerContactId: -1 }); 
    const [showTransferJobModal, setShowTransferJobModal] = React.useState(false);
    const [canReopenJob, setCanReopenJob] = React.useState(false);
    const [canCancelJob, setCanCancelJob] = React.useState(false);
    const [canUnlockJob, setCanUnlockJob] = React.useState(false);

    const initJobDetails: JobDetails = {
        id: 0,
        companyId: 0,
        customerContactFirstName: "",
        customerContactLastName: "",
        customerContactPhone: "",
        customerContactEmail: "",
        customerId: -1,
        customerName: "",
        customerContactId: -1,
        customerCashSaleAccount: false,
        accountTypeId: -1,
        accountType: "",
        accountLimitFormatted: "",
        siteAddressExists: true,
        siteAddress: "",
        siteAddressDistance: 0,
        siteAddressDistanceFormatted: "",
        siteAddressDuration: 0,
        siteAddressDurationFormatted: "",
        addressNearSiteAddress: "",
        siteAddressFull: "",
        unitNumber: "",
        streetNumber: "",
        streetName: "",
        suburb: "",
        state: "",
        country: "",
        postCode: "",
        manualStreetName: "",
        manualSuburb: "",
        manualState: "VIC",
        manualCountry: "Australia",
        manualPostCode: "",
        lotNumber: "",
        isEvent: false,
        isThreeDayHire: false,
        eventName: "",
        eventStartDate: "",
        eventEndDate: "",
        deliveryWindowStart: "",
        deliveryWindowEnd: "",
        deliveryPlacementNotes: "",
        jobType: -1,
        paymentType: -1,
        gateCode: "",
        googlePlaceId: "",
        siteAddressLatitude: 0,
        siteAddressLongitude: 0,
        addressGooglePlaceId: "",
        addressSiteAddressLatitude: 0,
        addressSiteAddressLongitude: 0,
        addressSiteAddressDistance: 0,
        addressSiteAddressDuration: 0,
        closestBranchId: 1,
        truckAccess: -1,
        parking: -1,
        siteClean: -1,
        powerlinesWithinRange: -1,
        weekdayAccess: -1,
        weekendAccess: -1,
        phoneCallRequired: -1,
        linkedToReckon: true,
        locked: false,
        statusId: 1,
        status: "Initial Enquiry",
        reference: "",
        goAheadOverride: false
    };
    const [jobDetails, setJobDetails] = React.useState<JobDetails>(initJobDetails);

    React.useEffect(() => {
        window.scrollTo(0, 0);
        let Id = Number(id);

        getData(Id);
    }, []);

    const getData = async (JobId: number) => {
        const apiUrl = 'GetJobDetailsScreen?JobId=' + JobId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setSystemCompanyId(result.data.systemCompanyId);
            setCanReopenJob(result.data.canReopenJobs);
            setCanCancelJob(result.data.canCancelJob);
            setCanUnlockJob(result.data.canUnlockJob);
            setJobTypeOptions(result.data.jobTypes);
            setBranches(result.data.branches);
            setYesNoOptions(result.data.yesNoOptions);
            setCustomerContacts(result.data.customerContacts);
            setJobDetails(result.data.jobDetails);
            setLoading(false);
        }
    }

    const clearErrors = (errorName: string) => {
        let errorList = errors;
        if (errorName) {
            delete errorList[errorName];
            setErrors(errorList);
        }
    }

    const handleChange = (e: any) => {
        clearErrors(e.target.name);
        setJobDetails(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    const handleCustomerContactChange = (e: any) => {
        e.preventDefault();

        clearErrors(e.target.name);
        var custContactId = e.target.value * 1;
        setJobDetails(prevState => ({ ...prevState, customerContactId: custContactId }));

        if (custContactId > 0) {
            //check to see if there is an email address, and if not then show edit contact box so user can add email
            var contact = customerContacts.find(cc => cc.id == custContactId);
            if (contact) {
                var custFirstName = contact.firstName;
                var custLastName = contact.lastName;
                var custContactPhone = contact.mobile == "" ? contact.phone : contact.mobile;
                var custEmail = contact.email;

                setJobDetails(prevState => ({ ...prevState, customerContactFirstName: custFirstName, customerContactLastName: custLastName, customerContactPhone: custContactPhone, customerContactEmail: custEmail }));
            }
        }
    }

    const handleCheckboxChange = (e: any) => {
        clearErrors(e.target.name);

        if (e.target.name === "isEvent" || e.target.name === "isThreeDayHire") {
            //don't want both selected
            if (e.target.name === "isEvent") {
                jobDetails.isThreeDayHire = false;
            } else if (e.target.name === "isThreeDayHire") {
                jobDetails.isEvent = false;
            }
        }

        setJobDetails(prevState => ({ ...prevState, [e.target.name]: e.target.checked }));

        if (!jobDetails.isEvent && !jobDetails.isThreeDayHire) {
            //reset event fields
            setJobDetails(prevState => ({...prevState, eventStartDate: "", eventEndDate: "", deliveryWindowStart: "", deliveryWindowEnd: ""          }));
        }
    }

    const handleAddressExistsChange = (e: any) => {
        //clear site address details when switching
        setJobDetails(prevState => ({
            ...prevState,
            siteAddressExists: !e.target.checked,
            siteAddress: "",
            siteAddressDistance: 0,
            siteAddressDistanceFormatted: "",
            siteAddressDuration: 0,
            siteAddressDurationFormatted: "",
            streetNumber: "",
            streetName: "",
            suburb: "",
            state: "",
            country: "",
            postCode: "",
            manualStreetName: "",
            manualSuburb: "",
            manualState: "VIC",
            manualCountry: "Australia",
            manualPostCode: ""
        }));
    }

    const setAddress = async (addressDetails: AddressDetails) => {
        setJobDetails(prevState => ({
            ...prevState,
            siteAddress: addressDetails.fullAddress,
            streetNumber: addressDetails.street_number,
            streetName: addressDetails.route,
            suburb: addressDetails.locality,
            state: addressDetails.administrative_area_level_1,
            country: addressDetails.country,
            postCode: addressDetails.postal_code,
            googlePlaceId: addressDetails.placeId,
            siteAddressLatitude: addressDetails.latitude,
            siteAddressLongitude: addressDetails.longitude,
        }));

        var addDuration = await UtilsGoogleMaps.calculateAddressDuration(branches, addressDetails);
        setJobDetails(prevState => ({
            ...prevState,
            siteAddressDistance: addDuration.distance,
            siteAddressDistanceFormatted: addDuration.distance + " km",
            siteAddressDuration: addDuration.duration,
            siteAddressDurationFormatted: addDuration.duration + " mins",
            closestBranchId: addDuration.branchId
        }));
    }

    const setNearestAddress = async (addressDetails: AddressDetails) => {
        var addDuration = await UtilsGoogleMaps.calculateAddressDuration(branches, addressDetails);

        setJobDetails(prevState => ({
            ...prevState,
            addressSiteAddressDistance: addDuration.distance,
            addressSiteAddressDuration: addDuration.duration,
            closestBranchId: addDuration.branchId
        }));
    }

    const hideCancelModal = () => {
        setShowCancelModal(false);
    }

    const openCancelModal = () => {
        //show popup to select reason
        setShowCancelModal(true);
    }

    const saveCancel = () => {
        setJobDetails(prevState => ({ ...prevState, statusId: 7, status: "Cancelled" }));
        setShowCancelModal(false);
        toast.success("Job Cancelled");
    }

    const reopenJob = (e: any) => {
        e.preventDefault();
        reopenJobDetails();
    }

    const reopenJobDetails = async () => {
        var openJob = {
            jobId: jobDetails.id
        };

        var jsonItem = JSON.stringify(openJob);

        const apiUrl = "ReopenJob";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            setJobDetails(prevState => ({ ...prevState, statusId: result.data }));
            toast.success("Job Re-opened Successfully");
        }
    }

    const hideAddContactModal = () => {
        setAddNewContact({ customerId: -1, customerContactId: -1 });
        setShowAddContactModal(false);
    }

    const openAddContactModal = (e: any) => {
        e.preventDefault();
        //show popup to add contact
        if (jobDetails.customerId && jobDetails.customerId > 0) {
            setAddNewContact({ customerId: jobDetails.customerId, customerContactId: -1 });
            setShowAddContactModal(true)
        } else {
            toast.error("Please select a Customer before adding a Contact");
        }
    }

    const showEditContactModal = (e: any) => {
        e.preventDefault();
        //show popup to edit contact
        if (jobDetails.customerId && jobDetails.customerId > 0) {
            editContactModal();
        } else {
            toast.error("Please select a Customer before editing a Contact");
        }
    }

    const editContactModal = () => {
        var contactId = (jobDetails.customerContactId ?? -1) * 1;
        setAddNewContact({ customerId: jobDetails.customerId ?? -1, customerContactId: contactId });
        setShowAddContactModal(true);
    }

    const saveAddContact = (contactDetails: CustomerContactList) => {
        setJobDetails(prevState => ({ ...prevState, customerContactId: contactDetails.id }));
        var contactList = [...customerContacts];

        if (addNewContact.customerContactId > 0) {
            //update customer details
            var existContact = contactList.find(cc => cc.id == addNewContact.customerContactId);
            if (existContact) {
                existContact.name = contactDetails.name;
            }
            setCustomerContacts(contactList);
        } else {
            //add the new contact details to the list
            contactList.push(contactDetails);
            setCustomerContacts(contactList);
            //when we add a new contact then auto update email address as they had to enter it when creating the contact
            setJobDetails(prevState => ({ ...prevState, customerContactEmail: contactDetails.email }));
        }

        //reset add contact
        setAddNewContact(prevState => ({ ...prevState, customerId: -1, customerContactId: -1 }));
        setShowAddContactModal(false);
    }

    //Transfer Job
    const showTransferModal = (e: any) => {
        e.preventDefault();
        setShowTransferJobModal(true);
    }

    const hideTransferJobModal = () => {
        setShowTransferJobModal(false);
    }

    const saveTransferJobModal = (updatedCustomerContacts: CustomerContactList[], jobId: number, newCustomerId: number, newCustomerName: string, newContactId: number) => {
        setShowTransferJobModal(false);
        toast.success("Job has been transferred successfully!");

        //stay on this job and reload contacts as they have changed to new customer
        if (updatedCustomerContacts.length > 0) {
            //we only what to update the customer contacts if we update the current job
            setCustomerContacts(updatedCustomerContacts);
            var custName = newCustomerName === "" ? jobDetails.customerName : newCustomerName;
            setJobDetails(prevState => ({ ...prevState, customerId: newCustomerId, customerName: custName, customerContactId: newContactId }));
        } else {
            //update job to cancelled status
            setJobDetails(prevState => ({ ...prevState, statusId: 7, status: "Cancelled" }));
            //this job has been cancelled so open new job
            var newJobUrl = "/job/" + jobId + "/details";
            window.open(newJobUrl, "_blank");
        }
    }
    //End Transfer Job

    const validate = () => {
        let fields = jobDetails;
        let errorList: any = {};
        let formIsValid = true;


        if (!fields.customerId || fields.customerId < 1) {
            formIsValid = false;
            errorList["customerId"] = "Customer is required";
        }

        if (fields.customerCashSaleAccount) {
            //cash sales accounts use customer name, phone and email
            var contactName = (fields.customerContactFirstName + fields.customerContactLastName).trim();
            if (!contactName) {
                formIsValid = false;
                errorList["customerContact"] = "Customer Contact First/Last Name is required";
            }
            if (!fields.customerContactPhone) {
                formIsValid = false;
                errorList["customerContactPhone"] = "Customer Phone is required";
            }
            if (!fields.customerContactEmail) {
                formIsValid = false;
                errorList["customerContactEmail"] = "Customer Email is required";
            } else if (!fields.customerContactEmail.toLowerCase().includes("@")) {
                formIsValid = false;
                errorList["customerContactEmail"] = "Valid Customer Contact Email is required";
            }
        } else {
            if (!fields.customerContactId || fields.customerContactId < 1) {
                formIsValid = false;
                errorList["customerContactId"] = "Customer Contact is required";
            }
        }

        if (fields.siteAddressExists && !fields.siteAddress) {
            formIsValid = false;
            errorList["siteAddress"] = "Valid Site Address is required";
        } else if (!fields.siteAddressExists) {
            if (!fields.manualStreetName) {
                formIsValid = false;
                errorList["manualStreetName"] = "Manual Street Name is required";
            }
            if (!fields.manualSuburb) {
                formIsValid = false;
                errorList["manualSuburb"] = "Manual Suburb is required";
            }
            if (!fields.manualState) {
                formIsValid = false;
                errorList["manualState"] = "Manual State is required";
            }
        }

        if (fields.isEvent || fields.isThreeDayHire) {
            if (!fields.eventName || fields.eventName === "") {
                formIsValid = false;
                var fieldName = fields.isEvent ? "Event " : "Invoicee ";
                errorList["eventName"] = fieldName + "Name is required";
            }

            //Add validation back in when we switch on extra event info
            //if (!fields.eventStartDate || fields.eventStartDate === "") {
            //    formIsValid = false;
            //    errorList["eventStartDate"] = "Event Start Date is required";
            //} else if (!DateHelpers.isValidDate(fields.eventStartDate)) {
            //    formIsValid = false;
            //    errorList["eventStartDate"] = "Valid Event Start Date is required";
            //}

            //if (!fields.eventEndDate || fields.eventEndDate === "") {
            //    formIsValid = false;
            //    errorList["eventEndDate"] = "Event End Date is required";
            //} else if (!DateHelpers.isValidDate(fields.eventEndDate)) {
            //    formIsValid = false;
            //    errorList["eventEndDate"] = "Valid Event End Date is required";
            //} else if (!DateHelpers.compareDateGreater(fields.eventStartDate, fields.eventEndDate)) {
            //    formIsValid = false;
            //    errorList["eventEndDate"] = "Event End Date > Event Start Date";
            //}

            //if (!fields.deliveryWindowStart || fields.deliveryWindowStart === "") {
            //    formIsValid = false;
            //    errorList["deliveryWindowStart"] = "Delivery Window Start is required";
            //} else if (!DateHelpers.isValidDate(fields.deliveryWindowStart)) {
            //    formIsValid = false;
            //    errorList["deliveryWindowStart"] = "Valid Delivery Window Start is required";
            //}

            //if (!fields.deliveryWindowEnd || fields.deliveryWindowEnd === "") {
            //    formIsValid = false;
            //    errorList["deliveryWindowEnd"] = "Delivery Window End is required";
            //} else if (!DateHelpers.isValidDate(fields.deliveryWindowEnd)) {
            //    formIsValid = false;
            //    errorList["deliveryWindowEnd"] = "Valid Delivery Window End is required";
            //} else if (!DateHelpers.compareDateGreater(fields.deliveryWindowStart, fields.deliveryWindowEnd)) {
            //    formIsValid = false;
            //    errorList["deliveryWindowEnd"] = "Delivery Window End > Delivery Window Start";
            //}
        }

        if (fields.jobType <= 0) {
            formIsValid = false;
            errorList["jobType"] = "Job Type is required";
        }

        if (systemCompanyId === 2) {
            if (fields.truckAccess < 0) {
                formIsValid = false;
                errorList["truckAccess"] = "Truck Access is required";
            }

            if (fields.parking < 0) {
                formIsValid = false;
                errorList["parking"] = "Parking Available is required";
            }

            if (fields.siteClean < 0) {
                formIsValid = false;
                errorList["siteClean"] = "Is Site Clean is required";
            }
        } else if (systemCompanyId === 3) {
            if (fields.weekdayAccess < 0) {
                formIsValid = false;
                errorList["weekdayAccess"] = "Weekday Access is required";
            }

            if (fields.weekendAccess < 0) {
                formIsValid = false;
                errorList["weekendAccess"] = "Weekend Access is required";
            }

            if (fields.phoneCallRequired < 0) {
                formIsValid = false;
                errorList["phoneCallRequired"] = "Phone Call is required";
            }
        }

        if (fields.powerlinesWithinRange < 0) {
            formIsValid = false;
            errorList["powerlinesWithinRange"] = "Powerlines within 4.6m is required";
        }

        if (!fields.siteAddressExists && fields.addressSiteAddressDuration === 0) {
            formIsValid = false;
            errorList["addressSiteAddressDuration"] = "Mins to Site Address required";
        }

        setErrors(errorList);
        return formIsValid;
    }

    const contactSubmit = (e: any) => {
        e.preventDefault();
        if (validate()) {
            saveJob();
        } else {
            toast.error("Check and fix errors before saving")
        }

    }

    const saveJob = () => {
        //need to save here
        if (validate()) {
            saveJobDetails();
        } else {
            toast.error("Check and fix errors before saving!");
        }
    }

    const saveJobDetails = async () => {
        var saveJobData = {
            jobDetails: jobDetails
        };

        var jsonItem = JSON.stringify(saveJobData);

        const apiUrl = "SaveJobDetails";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errorList = result.data.messages as any[];
                errorList.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                toast.success("Job Saved");
            }
        }
    }

    const tabChanged = (url: string) => {
        navigate(url);
    }

    const jobTab = <JobTabsData jobId={Number(id)} siteAddress={jobDetails.siteAddressFull} tab={"details"} tabChanged={tabChanged} />;
    var googleContents = <GooglePlacesDropdown addressId="autocomplete" addressLabel={"Site Address"} placeholder={jobDetails.siteAddress} className="input-group" useLargeLabel={false} afterValid={setAddress} />
    var googleAddressNearSite = <GooglePlacesDropdown addressId="autocompletenearsite" addressLabel={"Address Near Site"} placeholder={jobDetails.addressNearSiteAddress} className="input-group" useLargeLabel={false} afterValid={setNearestAddress} />
    const addEditContact = <JobDetailsAddContactModalData showModal={showAddContactModal} hideModal={hideAddContactModal} customerId={addNewContact.customerId} id={addNewContact.customerContactId} saveModal={saveAddContact} />
    const transferJob = <JobDetailsTransferJobModalData showModal={showTransferJobModal} hideModal={hideTransferJobModal} jobId={jobDetails.id} systemCompanyId={systemCompanyId} saveModal={saveTransferJobModal} />
    const cancelJob = <CancelJobPopup jobId={jobDetails.id} showModal={showCancelModal} hideModal={hideCancelModal} saveModal={saveCancel} />

    const renderDetails = (
        <div>
            <div className="static-modal">
                {addEditContact}
                {transferJob}
                {cancelJob}
            </div>
            {jobTab}

            <div>
                <label className={systemCompanyId === 3 ? "input-group--inline" : "hidden"} htmlFor="isEvent">
                    <span className="label">Is job for an Event?</span>
                    <input className="checkbox" type="checkbox" id="isEvent" name="isEvent" checked={jobDetails.isEvent} onChange={(e) => handleCheckboxChange(e)}></input>
                </label>

                <label className={systemCompanyId === 3 ? "input-group--inline" : "hidden"} htmlFor="isThreeDayHire">
                    <span className="label">Or Short-Term Hire?</span>
                    <input className="checkbox" type="checkbox" id="isThreeDayHire" name="isThreeDayHire" checked={jobDetails.isThreeDayHire} onChange={(e) => handleCheckboxChange(e)}></input>
                </label>
            </div>
            <div className={jobDetails.isEvent || jobDetails.isThreeDayHire ? "" : "hidden"}>
                <label className="input-group" htmlFor="eventName">
                    <span className="label">{jobDetails.isEvent ? "Event Name" : jobDetails.isThreeDayHire ? "Invoicee" : ""}</span>
                    <input className="input" type="text" id="eventName" name="eventName" value={jobDetails.eventName} onChange={(e) => handleChange(e)}></input>
                </label>
                <span className={errors["eventName"] ? "label errors marginLeftLabel" : "hidden"}>{errors["eventName"]}</span>
            </div>
            <div className="hidden">
                <label className="input-group" htmlFor="eventStartDate">
                    <span className="label">Event Start</span>
                    <input className="input" type="datetime-local" id="eventStartDate" name="eventStartDate" value={jobDetails.eventStartDate} onChange={(e) => handleChange(e)}></input>
                </label>
                <span className={errors["eventStartDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["eventStartDate"]}</span>
                <label className="input-group" htmlFor="eventEndDate">
                    <span className="label">Event End</span>
                    <input className="input" type="datetime-local" id="eventEndDate" name="eventEndDate" value={jobDetails.eventEndDate} onChange={(e) => handleChange(e)}></input>
                </label>
                <span className={errors["eventEndDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["eventEndDate"]}</span>
                <div className="marginLeftLabel">Enter the earliest date and time that the product/s can be delivered.</div>
                <label className="input-group" htmlFor="deliveryWindowStart">
                    <span className="label">Delivery Window Start</span>
                    <input className="input" type="datetime-local" id="deliveryWindowStart" name="deliveryWindowStart" value={jobDetails.deliveryWindowStart} onChange={(e) => handleChange(e)}></input>
                </label>
                <span className={errors["deliveryWindowStart"] ? "label errors marginLeftLabel" : "hidden"}>{errors["deliveryWindowStart"]}</span>
                <div className="marginLeftLabel">Enter the latest date and time that the product/s can be delivered.</div>
                <label className="input-group" htmlFor="deliveryWindowEnd">
                    <span className="label">Delivery Window End</span>
                    <input className="input" type="datetime-local" id="deliveryWindowEnd" name="deliveryWindowEnd" value={jobDetails.deliveryWindowEnd} onChange={(e) => handleChange(e)}></input>
                </label>
                <span className={errors["deliveryWindowEnd"] ? "label errors marginLeftLabel" : "hidden"}>{errors["deliveryWindowEnd"]}</span>
                <hr></hr>
            </div>
            <div>
                <label className="input-group" htmlFor="customer">
                    <span className="label">Customer</span>
                    <input className="input marginRight10" type="text" id="customerName" name="customerName" value={jobDetails.customerName} disabled></input>
                    <a className={jobDetails.customerId && jobDetails.customerId > 0 ? "marginRight10" : "hidden"} href={"/customer/" + jobDetails.customerId} target="_blank">View</a>
                    <a className={jobDetails.id > 0 && jobDetails.customerId && jobDetails.customerId > 0 ? "" : "hidden"} href="#" onClick={(e) => showTransferModal(e)}>Transfer</a>
                </label>
                <span className={jobDetails.accountLimitFormatted === "" ? "hidden" : "marginLeftLabel"}>{jobDetails.accountTypeId === 1 ? jobDetails.accountType : jobDetails.accountType + ' - ' + jobDetails.accountLimitFormatted}</span>
                <span className={jobDetails.locked ? "makeitred makeitbold marginLeft20" : "hidden"}>Customer has been LOCKED by Accounts!</span>
            </div>
            <div className={jobDetails.customerCashSaleAccount ? "hidden" : ""}>
                <label className="input-group" htmlFor="contact">
                    <span className="label">Contact</span>
                    <select className="select marginRight10" id="contact" name="customerContactId" value={jobDetails.customerContactId} onChange={(e) => handleCustomerContactChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {customerContacts.map(custcon =>
                            <option key={custcon.id} value={custcon.id}>{custcon.name}</option>
                        )};
                    </select>
                    <a className={jobDetails.customerContactId && jobDetails.customerContactId > 0 ? "marginRight10" : "hidden"} href="#" onClick={(e) => showEditContactModal(e)}>Edit</a>
                    <a className={jobDetails.customerId && jobDetails.customerId > 0 ? "" : "hidden"} href="#" onClick={(e) => openAddContactModal(e)}>Add New</a>
                </label>
                <span className={errors["customerContactId"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerContactId"]}</span>
            </div>

            <label className={jobDetails.customerContactId && jobDetails.customerContactId > 0 && jobDetails.id === 0 ? "input-group" : "hidden"} htmlFor="customerContactEmail">
                <span className="label">Customer Email</span>
                <input className="input marginRight10" type="text" maxLength={200} id="customerContactEmail" name="customerContactEmail" value={jobDetails.customerContactEmail} onChange={(e) => handleChange(e)}></input>
            </label>
            <span className={errors["customerContactConfirmEmail"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerContactConfirmEmail"]}</span>

            <div className={!jobDetails.siteAddressExists ? "hidden" : ""}>
                {googleContents}
                <span className={errors["siteAddress"] ? "label errors marginLeftLabel" : "hidden"}>{errors["siteAddress"]}</span>
                <div className="marginLeftLabel">Distance: {jobDetails.siteAddressDistanceFormatted} Duration: {jobDetails.siteAddressDurationFormatted}</div>
            </div>
            <div>
                <label className="input-group" htmlFor="siteAddrExists">
                    <span className="label label--setsizesm">Site Address Doesn't Exist</span>
                    <input className="checkbox" type="checkbox" id="siteAddrExists" name="siteAddressExists" checked={!jobDetails.siteAddressExists} onChange={(e) => handleAddressExistsChange(e)}></input>
                </label>
                <div className={jobDetails.siteAddressExists ? "hidden" : ""}>
                    <label className="input-group" htmlFor="manualStreetName">
                        <span className="label">Site Street No/Name</span>
                        <input className="input" type="text" id="manualStreetName" name="manualStreetName" value={jobDetails.manualStreetName} onChange={(e) => handleChange(e)}></input>
                    </label>
                    <span className={errors["manualStreetName"] ? "label errors marginLeftLabel" : "hidden"}>{errors["manualStreetName"]}</span>

                    <label className="input-group" htmlFor="manualSuburb">
                        <span className="label">Site Suburb</span>
                        <input className="input" type="text" maxLength={40} id="manualSuburb" name="manualSuburb" value={jobDetails.manualSuburb} onChange={(e) => handleChange(e)}></input>
                    </label>
                    <span className={errors["manualSuburb"] ? "label errors marginLeftLabel" : "hidden"}>{errors["manualSuburb"]}</span>

                    <label className="input-group" htmlFor="manualPostCode">
                        <span className="label">Site Post Code</span>
                        <input className="input" type="text" id="manualPostCode" name="manualPostCode" value={jobDetails.manualPostCode} onChange={(e) => handleChange(e)}></input>
                    </label>
                    <span className={errors["manualPostCode"] ? "label errors marginLeftLabel" : "hidden"}>{errors["manualPostCode"]}</span>

                    <label className="input-group" htmlFor="manualState">
                        <span className="label">State</span>
                        <input className="input" maxLength={5} type="text" id="manualState" name="manualState" value={jobDetails.manualState} onChange={(e) => handleChange(e)}></input>
                    </label>
                    <span className={errors["manualState"] ? "label errors marginLeftLabel" : "hidden"}>{errors["manualState"]}</span>
                </div>
            </div>
            <div className={jobDetails.siteAddressExists ? "hidden" : ""}>
                {googleAddressNearSite}
                <span className="label errors marginLeftLabel">{errors["addressNearSiteAddress"]}</span>

                <label className="input-group" htmlFor="addressSiteAddressDuration">
                    <span className="label">Mins to Site Address (one way)</span>
                    <input className="input" type="text" id="addressSiteAddressDuration" name="addressSiteAddressDuration" value={jobDetails.addressSiteAddressDuration} onChange={(e) => handleChange(e)}></input>
                </label>
                <span className={errors["addressSiteAddressDuration"] ? "label errors marginLeftLabel" : "hidden"}>{errors["addressSiteAddressDuration"]}</span>
            </div>
            <div>
                <label className="input-group" htmlFor="unitno">
                    <span className="label">Unit Number</span>
                    <input className="input" type="text" maxLength={10} id="unitno" name="unitNumber" value={jobDetails.unitNumber} onChange={(e) => handleChange(e)}></input>
                </label>
            </div>
            <div>
                <label className="input-group" htmlFor="lotno">
                    <span className="label">Lot Number</span>
                    <input className="input" type="text" id="lotno" name="lotNumber" value={jobDetails.lotNumber} onChange={(e) => handleChange(e)}></input>
                </label>
            </div>
            <div className={systemCompanyId === 3 ? "" : "hidden"}>
                <div className="marginLeftLabel">Enter any notes for delivery and/or placement of products for driver, <br></br> e.g. Shop Name, Access from [Street Name], Gate [Number] etc.</div>
                <label className="input-group" htmlFor="deliveryPlacementNotes">
                    <span className="label">Delivery/Placement Notes</span>
                    <textarea className="input" rows={2} id="deliveryPlacementNotes" name="deliveryPlacementNotes" value={jobDetails.deliveryPlacementNotes} onChange={(e) => handleChange(e)}></textarea>
                </label>
            </div>
            <div>
                <label className="input-group" htmlFor="jobtype">
                    <span className="label">Job Type</span>
                    <select className="select" id="jobtype" name="jobType" value={jobDetails.jobType} onChange={(e) => handleChange(e)} disabled={jobDetails.id === 0}>
                        <option hidden defaultValue="-1"></option>
                        {jobTypeOptions.map(jobType =>
                            <option key={jobType.id} value={jobType.id}>{jobType.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["jobType"] ? "label errors marginLeftLabel" : "hidden"}>{errors["jobType"]}</span>
            </div>
            <div className={jobDetails.id > 0 && jobDetails.accountTypeId === 1 ? "" : "hidden" }>
                <label className="input-group" htmlFor="paymentType">
                    <span className="label">Payment Method</span>
                    <select className="select" id="paymentType" name="paymentType" value={jobDetails.paymentType} onChange={(e) => handleChange(e)} disabled={jobDetails.id === 0}>
                        <option defaultValue="-1" value={-1}></option>
                        <option value={3}>Credit Card</option>
                        <option value={4}>Direct Debit</option>
                    </select>
                </label>
            </div>
            <div>
                <label className="input-group" htmlFor="gatecode">
                    <span className="label">Gate Code</span>
                    <input className="input" type="text" maxLength={20} id="gatecode" name="gateCode" value={jobDetails.gateCode} onChange={(e) => handleChange(e)}></input>
                </label>
                <label className="input-group" htmlFor="reference">
                    <span className="label">Reference</span>
                    <input className="input" type="text" maxLength={200} id="reference" name="reference" value={jobDetails.reference} onChange={(e) => handleChange(e)}></input>
                </label>
            </div>
            <div className={systemCompanyId === 3 ? "hidden" : ""}>
                <div>
                    <label className="input-group" htmlFor="truck">
                        <span className="label">Truck Access?</span>
                        <select className="select" name="truckAccess" value={jobDetails.truckAccess} onChange={(e) => handleChange(e)}>
                            <option hidden defaultValue="-1"></option>
                            {yesNoOptions.map(yn =>
                                <option key={yn.id} value={yn.id}>{yn.name}</option>
                            )};
                        </select>
                    </label>
                    <span className={errors["truckAccess"] ? "label errors marginLeftLabel" : "hidden"}>{errors["truckAccess"]}</span>
                </div>
                <div>
                    <label className="input-group" htmlFor="parking">
                        <span className="label">Parking Avail?</span>
                        <select className="select" id="parking" name="parking" value={jobDetails.parking} onChange={(e) => handleChange(e)}>
                            <option hidden defaultValue="-1"></option>
                            {yesNoOptions.map(yn =>
                                <option key={yn.id} value={yn.id}>{yn.name}</option>
                            )};
                        </select>
                    </label>
                    <span className={errors["parking"] ? "label errors marginLeftLabel" : "hidden"}>{errors["parking"]}</span>
                </div>
                <div>
                    <label className="input-group" htmlFor="siteclean">
                        <span className="label">Is Site {systemCompanyId === 2 ? "Clean" : "Level"}?</span>
                        <select className="select" id="siteclean" name="siteClean" value={jobDetails.siteClean} onChange={(e) => handleChange(e)}>
                            <option hidden defaultValue="-1"></option>
                            {yesNoOptions.map(yn =>
                                <option key={yn.id} value={yn.id}>{yn.name}</option>
                            )};
                        </select>
                    </label>
                    <span className={errors["siteClean"] ? "label errors marginLeftLabel" : "hidden"}>{errors["siteClean"]}</span>
                </div>
            </div>
            <div>
                <label className="input-group" htmlFor="powerlines">
                    <span className="label">Powerlines within 4.6m?</span>
                    <select className="select" id="powerlines" name="powerlinesWithinRange" value={jobDetails.powerlinesWithinRange} onChange={(e) => handleChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["powerlinesWithinRange"] ? "label errors marginLeftLabel" : "hidden"}>{errors["powerlinesWithinRange"]}</span>
            </div>
            <div className={systemCompanyId === 3 ? "" : "hidden"}>
                <label className="input-group" htmlFor="weekdayAccess">
                    <span className="label">Is there clear access between 7am and 3pm weekdays?</span>
                    <select className="select" id="weekdayAccess" name="weekdayAccess" value={jobDetails.weekdayAccess} onChange={(e) => handleChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["weekdayAccess"] ? "label errors marginLeftLabel" : "hidden"}>{errors["weekdayAccess"]}</span>

                <label className="input-group" htmlFor="weekendAccess">
                    <span className="label">Is there weekend access?</span>
                    <select className="select" id="weekendAccess" name="weekendAccess" value={jobDetails.weekendAccess} onChange={(e) => handleChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["weekendAccess"] ? "label errors marginLeftLabel" : "hidden"}>{errors["weekendAccess"]}</span>

                <label className="input-group" htmlFor="phoneCallRequired">
                    <span className="label">Phone call required prior to arrival?</span>
                    <select className="select" id="phoneCallRequired" name="phoneCallRequired" value={jobDetails.phoneCallRequired} onChange={(e) => handleChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["phoneCallRequired"] ? "label errors marginLeftLabel" : "hidden"}>{errors["phoneCallRequired"]}</span>
            </div>
            <div>
                <label className="input-group" htmlFor="statusId">
                    <span className="label">Status</span>
                    <input className="input" type="text" value={jobDetails.status} readOnly disabled></input>
                </label>
            </div>
            <div className={jobDetails.id > 0 && canUnlockJob && jobDetails.locked ? "" : "hidden"}>
                <label className="input-group" htmlFor="goAheadOverride">
                    <span className="label label--setsizesm">Job can goahead (locked customer)</span>
                    <input className="checkbox" type="checkbox" id="goAheadOverride" name="goAheadOverride" checked={jobDetails.goAheadOverride} onChange={(e) => handleCheckboxChange(e)}></input>
                </label>
            </div>
         

            <div className={jobDetails.id === 0 ? "defaultbutton__container--right marginBottom10" : "marginBottom10"}>
                <button className={jobDetails.statusId == 7 ? "hidden" : "defaultbutton defaultbutton-prev"} type="button" onClick={openCancelModal} disabled={!canCancelJob}>Cancel Job</button>
                <button className={jobDetails.statusId == 7 && canReopenJob ? "defaultbutton defaultbutton-prev" : "hidden"} type="button" onClick={(e) => reopenJob(e)}>Re-open Job</button>
                <button className="defaultbutton defaultbutton--save marginLeft10" type="button" onClick={contactSubmit}>Save</button>
            </div>

        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}
export default JobDetailData;